import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { getBuildingTypes } from 'features/bpList/ducks/selectors'
import { propOr, values } from 'ramda'
import { ReactComponent as KeyIcon } from 'assets/images/icon-key.svg'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import QrCodePreviewModal from 'features/qrLanding/QrCodePreviewModal'
import UnitForm from 'features/createBp/components/atoms/UnitForm'
import FloorBadges from 'features/bpDetails/Components/files/FloorBadges'
import Modal from 'components/atoms/Modal'
import {
  clearSelectedUnitRoutine,
  removeBpUnitRoutine,
  updateBpUnitRoutine
} from 'ducks/units/actions'
import { hasMultipleUnits } from 'utils/units'
import { selectBpUnits } from 'ducks/units/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import UnitEpcBadge from 'features/bpDetails/Components/units/UnitEpcBadge'

const UnitBox = ({ unit, canEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const dropdownOpen = Boolean(anchorEl)
  const buildingTypes = useSelector(getBuildingTypes)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const isSoleResidential = unit.buildingType === 'residential.dwelling.sole_unit'
  const isSoleUnit = !hasMultipleUnits(unit.buildingType)
  const bpUnits = useSelector(selectBpUnits)

  const unitsCount = useMemo(() => {
    if (bpUnits) {
      const typeUnits = bpUnits.filter(bpUnit => bpUnit.buildingType === unit.buildingType)
      return typeUnits.length
    } else {
      return 0
    }
  }, [bpUnits])

  const handleEditModalOpen = () => {
    setEditModalOpen(true)
  }
  const handleEditModalClose = () => {
    setEditModalOpen(false)
  }

  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true)
  }
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false)
  }

  const handleRemoveUnit = values => () => {
    dispatch(removeBpUnitRoutine(values))
    dispatch(clearSelectedUnitRoutine())
    handleDeleteModalClose()
  }

  const handleUpdateUnit = values => {
    dispatch(updateBpUnitRoutine({
      ...values,
      bpId: bp.id,
      updateSelected: true
    }))
  }

  const openMenu = e => {
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleCopyAlias = () => {
    if (!unit.inboxAlias) return
    navigator.clipboard?.writeText(unit.inboxAlias)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const buildingType = useMemo(() => {
    if (buildingTypes) {
      const type = values(buildingTypes).find(type => type.key === unit.buildingType)
      return propOr('', 'label', type)
    }
  }, [buildingTypes])

  return (
    <UnitBoxWrapper>
      <UnitBoxHeader>
        <UnitBasicDetails>
          <UnitBoxImage>
            <KeyIcon />
          </UnitBoxImage>
          <UnitTextContent>
            <UnitBoxUnitName>
              {unit.name}
            </UnitBoxUnitName>
            <UnitBoxUprn>
              UPRN: {unit.uprn || 'Not yet allocated'}
            </UnitBoxUprn>
            {unit.plotNumber && (
              <UnitBoxPlotNumber>
                Plot: {unit.plotNumber}
              </UnitBoxPlotNumber>
            )}
          </UnitTextContent>
        </UnitBasicDetails>
        {
          canEdit && (
            <IconWrapper>
              <MoreIcon onClick={openMenu} />
              <Menu
                keepMounted
                anchorEl={anchorEl}
                style={{
                  marginTop: '10px'
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                open={dropdownOpen}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={handleEditModalOpen}
                  disabled={
                    isSoleResidential ||
                    (unitsCount === 1 && isSoleUnit)
                  }
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => { handleClose(); setQrOpen(true) }}
                >
                  <QrCode2OutlinedIcon style={{ fontSize: 18, marginRight: 6 }} />
                  Show QR code
                </MenuItem>
                <MenuItem
                  onClick={handleDeleteModalOpen}
                  disabled={
                    bpUnits.length <= 1 ||
                    isSoleResidential
                  }
                >
                  Remove Unit
                </MenuItem>
              </Menu>
            </IconWrapper>
          )
        }
      </UnitBoxHeader>
      {unit.inboxAlias && (
        <UnitEmailHint>
          <UnitEmailRow>
            <UnitEmailLabel>Email documents to this unit:</UnitEmailLabel>
            <UnitEmailValue title={unit.inboxAlias}>{unit.inboxAlias}</UnitEmailValue>
            <CopyEmailButton type='button' onClick={handleCopyAlias} title='Copy address'>
              {copied
                ? <CheckRoundedIcon style={{ fontSize: 16 }} />
                : <ContentCopyRoundedIcon style={{ fontSize: 15 }} />}
            </CopyEmailButton>
          </UnitEmailRow>
          <UnitEmailNote>Attachments sent here are filed to this unit automatically and matched to its requirements.</UnitEmailNote>
        </UnitEmailHint>
      )}
      <UnitMoreDetails>
        <Details>
          <Detail withMargin>Type: <span>{buildingType}</span></Detail>
          <Detail withMargin>
            Area:
            <span>
              {unit.area ? `${unit.area} sqm` : ' ---'}
            </span>
          </Detail>
          <Detail>
            EPC: <UnitEpcBadge unit={unit} compact />
          </Detail>
        </Details>
        <Floors>
          Floors:&nbsp;
          {
            unit.floorNumbers
              ? <FloorBadges floorNumbers={unit.floorNumbers} />
              : '---'
          }
        </Floors>
      </UnitMoreDetails>
      <Modal
        open={editModalOpen}
        title='Edit unit'
        onClose={handleEditModalClose}
      >
        <UnitForm
          unit={unit}
          closeEditModal={handleEditModalClose}
          onSubmit={handleUpdateUnit}
          bp={bp}
          buildingType={unit.buildingType}
          hideAssignments
        />
      </Modal>
      <Modal
        open={deleteModalOpen}
        title='Delete unit'
        onClose={handleDeleteModalClose}
        withCancel
        onCancel={handleDeleteModalClose}
        withSubmit
        onSubmit={handleRemoveUnit({ bpId: bp.id, unitId: propOr('', 'id', unit) })}
      >
        Do you want to remove <ModalUnitName>{propOr('', 'name', unit)}</ModalUnitName>?
        Removing the unit will have an impact on total unit number defined for this Building Passport.
      </Modal>
      {qrOpen && (
        <QrCodePreviewModal
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          kind="unit"
          bpId={bp?.id}
          unitId={unit.id}
          name={unit.name}
        />
      )}
    </UnitBoxWrapper>
  )
}

export default UnitBox

const UnitBoxWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 4px;
`

const UnitBoxHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`

const UnitBoxImage = styled.div`
  width: 56px;
  height: 56px;
  margin-right: 15px;
  background-color: #fbede9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    margin-right: 12px;
  }
`

const UnitTextContent = styled.div`
  flex: 1;
  min-width: 0;
`

const UnitBoxUnitName = styled.div`
  font-size: 19px;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`

const UnitBoxUprn = styled.div`
  font-size: 12px;
  opacity: .4;
`

const UnitBoxPlotNumber = styled.div`
  font-size: 12px;
  opacity: .4;
`

const UnitEmailHint = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: #f7f9fc;

  @media (max-width: 768px) {
    padding: 12px 15px;
  }
`

const UnitEmailRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const UnitEmailLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const UnitEmailValue = styled.span`
  font-family: monospace;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CopyEmailButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  cursor: pointer;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.grey[700]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const UnitEmailNote = styled.div`
  margin-top: 6px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const UnitBasicDetails = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
`

const MoreIcon = styled(MoreVertIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  font-size: 30px !important;
`

const UnitMoreDetails = styled.div`
  padding: 7px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[800]};
  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    color: ${({ theme }) => theme.colors.haiti};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const Details = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const Detail = styled.div`
  margin-right: ${({ withMargin }) => withMargin ? '20px' : 0};

  @media (max-width: 480px) {
    margin-right: ${({ withMargin }) => withMargin ? '12px' : 0};
  }
`

const Floors = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const ModalUnitName = styled.span`
  font-weight: bold;
`
